import { Undo2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { errorMessage, isApiError } from '@/lib/api/errors'
import type { AiCreditCharge, CustomerDetail } from '@/lib/api/types'
import { formatDateTime } from '@/lib/format'
import { useRefundAiCredits } from './api'

const STATUS = { charged: 'Charged', refunded: 'Given back', not_charged: 'Not charged (failed)' } as const
const SHOWN = 8

/**
 * Arva SEO AI credits the customer was charged in the last 60 days. AI work that fails gives its
 * credits back by itself; owners and admins give back one that was charged and still didn’t work.
 */
export function AiCreditsCard({ customer: c, canManage }: { customer: CustomerDetail; canManage: boolean }) {
  const [all, setAll] = useState(false)
  const [refunding, setRefunding] = useState<AiCreditCharge | null>(null)
  const charges = c.ai_credits?.charges
  if (!charges) return null
  const shown = all ? charges : charges.slice(0, SHOWN)

  return (
    <Card>
      <CardHeader
        title="AI credits"
        description="Charges for Arva SEO’s AI in the last 60 days. Failed work gives its credits back by itself; give back one that was charged and still didn’t work."
      />
      <CardBody>
        {charges.length === 0 ? (
          <p className="text-[13px] text-ink-subtle">No AI credits used in the last 60 days.</p>
        ) : (
          <>
            <ul className="grid divide-y divide-border text-[13px]" aria-label="AI credit charges">
              {shown.map((ch) => (
                <li key={ch.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="grid gap-0.5">
                    <span className="font-medium text-ink">
                      {ch.label} · {ch.credits} {ch.credits === 1 ? 'credit' : 'credits'}
                    </span>
                    <span className="text-xs text-ink-subtle">
                      {formatDateTime(ch.created_at)}
                      {ch.refunded_at && ` · given back ${formatDateTime(ch.refunded_at)}`}
                    </span>
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="text-xs text-ink-muted">{STATUS[ch.status]}</span>
                    {canManage && ch.refundable && c.closed_at === null && (
                      <Button size="sm" variant="secondary" onClick={() => setRefunding(ch)}>
                        <Undo2 /> Give back
                      </Button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            {charges.length > SHOWN && (
              <Button size="sm" variant="ghost" className="mt-2" onClick={() => setAll(!all)}>
                {all ? 'Show fewer' : `Show all ${charges.length}`}
              </Button>
            )}
          </>
        )}
      </CardBody>
      {refunding && <RefundDialog customer={c} charge={refunding} onClose={() => setRefunding(null)} />}
    </Card>
  )
}

function RefundDialog({
  customer: c,
  charge,
  onClose,
}: {
  customer: CustomerDetail
  charge: AiCreditCharge
  onClose: () => void
}) {
  const refund = useRefundAiCredits(c.id)
  const [reason, setReason] = useState('')
  const [notify, setNotify] = useState(true)
  const fieldError = (f: string) => (isApiError(refund.error) ? refund.error.firstFieldError(f) : undefined)
  const credits = `${charge.credits} AI ${charge.credits === 1 ? 'credit' : 'credits'}`

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title="Give back AI credits"
        description={`${charge.label} on ${formatDateTime(charge.created_at)}: ${credits} go back into ${c.name}’s AI credits for this month (top-up credits go back to their top-up).`}
        footer={
          <>
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button
              loading={refund.isPending}
              disabled={reason.trim() === ''}
              onClick={() =>
                refund.mutate(
                  { reservation_id: charge.id, reason: reason.trim(), notify },
                  {
                    onSuccess: () => {
                      toast.success(`Gave back ${credits}.`)
                      onClose()
                    },
                    onError: (e) => toast.error(errorMessage(e)),
                  },
                )
              }
            >
              Give back {credits}
            </Button>
          </>
        }
      >
        <div className="grid gap-4">
          <Field
            label="Reason"
            htmlFor="ai-refund-reason"
            required
            error={fieldError('reason')}
            hint="Shown to staff in the audit log, not the customer."
          >
            <Input
              id="ai-refund-reason"
              maxLength={200}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          <label className="flex items-center gap-2 text-[13px] text-ink">
            <input
              type="checkbox"
              className="size-4"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
            />
            Tell {c.owner?.email ?? 'the owner'} in their notifications
          </label>
        </div>
      </DialogContent>
    </Dialog>
  )
}
